// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  startWith,
  take,
  tap,
} from 'rxjs';
import { TimeFromNowPipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import {
  APICallState,
  ChangeInfo,
  RequestOptions,
} from '../../../shared/utils/api-call-state.util';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericApiCall = APICallState<any, any>;

@Component({
  selector: 'app-refresh-data-sources-button',
  templateUrl: './refresh-data-sources-button.component.html',
  styleUrls: ['./refresh-data-sources-button.component.css'],
  standalone: true,
  imports: [AsyncPipe, TimeFromNowPipe, TranslateModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RefreshDataSourcesButtonComponent implements OnChanges {
  // Empty component
  /**
   * api call used to mange template (can also take an array of calls)
   */
  @Input({ required: true }) apiCall!: GenericApiCall | GenericApiCall[];
  @Output() didChange = new EventEmitter<ChangeInfo<any>[]>();

  lastRefresh = new Date();

  loading$: Observable<boolean> = new BehaviorSubject(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.apiCall) {
      const calls = Array.isArray(this.apiCall) ? this.apiCall : [this.apiCall];

      // true as long as any api call is still loading
      this.loading$ = combineLatest([
        ...calls.map((call) => call.loading$.pipe(startWith(false))),
      ]).pipe(
        map((loading) => loading.some((l) => l)),
        tap((loading) => {
          if (!loading) {
            this.lastRefresh = new Date();
          }
        })
      );
    }
  }

  refresh(): void {
    const requestOptions: RequestOptions = {
      forceRefresh: true,
      clearStaleData: false,
    };
    const calls = Array.isArray(this.apiCall) ? this.apiCall : [this.apiCall];
    if (calls) {
      combineLatest(
        calls.map((call) => call.retryAndDetectChange(requestOptions)!)
      )
        .pipe(
          take(1),
          filter((changed) => !!changed)
        )
        .subscribe((changed) => {
          this.didChange.emit(changed);
        });
    }
  }
}
